import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { registerLocale } from "react-datepicker";
// Constants
import { dateFnsLocales } from "../../../../config/dates";
// Components
import {
  Overlay,
  Modal,
  ModalHeader,
  ModalTitle,
  SectionTitle,
  CloseButton,
  CloseIcon,
  Description,
  FieldsRow,
  Field,
  Label,
  Input,
  DateInput,
  ErrorMessage,
  Buttons,
  BackButton,
  ExportButton,
  ExportIcon,
} from "./styles";
import useOverlayClose from "../../../../config/useOverlayClose";
// Assets
import { cross, download } from "../../../../assets/icons";

// Las fechas se muestran con el formato del idioma de la pantalla.
Object.entries(dateFnsLocales).forEach(([language, locale]) => registerLocale(language, locale));

const textFields = ["centre", "contactPerson", "email", "phone"];

const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

// Compara solo el dia: la hora que arrastre el selector es irrelevante.
const startOfDay = (date) => new Date(date.getFullYear(), date.getMonth(), date.getDate());

const addDay = (date) => new Date(date.getFullYear(), date.getMonth(), date.getDate() + 1);

const QuoteDetails = ({ onClose, onExport }) => {
  const { t, i18n } = useTranslation();
  const overlayHandlers = useOverlayClose(onClose);

  // La fecha de emision se propone como hoy, que es el caso habitual.
  const [issueDate, setIssueDate] = useState(new Date());
  const [validUntil, setValidUntil] = useState(null);
  const [fields, setFields] = useState({ centre: "", contactPerson: "", email: "", phone: "" });

  const changeField = (field, value) => setFields((prev) => ({ ...prev, [field]: value }));

  const trimmedEmail = fields.email.trim();
  const hasEmailError = !!trimmedEmail && !isValidEmail(trimmedEmail);
  const hasValidUntilError = !!issueDate && !!validUntil && startOfDay(validUntil) <= startOfDay(issueDate);

  const areFieldsFilled = textFields.every((field) => fields[field].trim());
  // El boton no se activa hasta que todo esta relleno y es coherente.
  const canExport = !!issueDate && !!validUntil && areFieldsFilled && !hasEmailError && !hasValidUntilError;

  const handleExport = () => {
    if (!canExport) return;

    onExport({
      issueDate,
      validUntil,
      centre: fields.centre.trim(),
      contactPerson: fields.contactPerson.trim(),
      email: trimmedEmail,
      phone: fields.phone.trim(),
    });
  };

  return (
    <Overlay {...overlayHandlers}>
      <Modal>
        <ModalHeader>
          <ModalTitle>{t("quote.title")}</ModalTitle>
          <CloseButton type="button" onClick={onClose} aria-label={t("budget.close")}>
            <CloseIcon src={cross} alt="" />
          </CloseButton>
        </ModalHeader>
        <Description>{t("quote.description")}</Description>

        <FieldsRow>
          <Field>
            <Label>{t("quote.issueDate")}</Label>
            <DateInput
              selected={issueDate}
              onChange={setIssueDate}
              locale={i18n.language}
              dateFormat="P"
              placeholderText={t("quote.issueDatePlaceholder")}
              withPortal
            />
          </Field>
          <Field>
            <Label>{t("quote.validUntil")}</Label>
            <DateInput
              selected={validUntil}
              onChange={setValidUntil}
              locale={i18n.language}
              dateFormat="P"
              placeholderText={t("quote.validUntilPlaceholder")}
              minDate={issueDate ? addDay(issueDate) : null}
              $hasError={hasValidUntilError}
              withPortal
            />
            {hasValidUntilError && <ErrorMessage>{t("quote.invalidValidUntil")}</ErrorMessage>}
          </Field>
        </FieldsRow>

        <SectionTitle>{t("quote.preparedFor")}</SectionTitle>
        <Description>{t("quote.preparedForDescription")}</Description>

        <FieldsRow>
          {textFields.map((field) => (
            <Field key={field}>
              <Label htmlFor={field}>{t(`quote.${field}`)}</Label>
              <Input
                id={field}
                type="text"
                placeholder={t(`quote.${field}Placeholder`)}
                value={fields[field]}
                $hasError={field === "email" && hasEmailError}
                onChange={(e) => changeField(field, e.target.value)}
              />
              {field === "email" && hasEmailError && <ErrorMessage>{t("quote.invalidEmail")}</ErrorMessage>}
            </Field>
          ))}
        </FieldsRow>

        <Buttons>
          <BackButton type="button" onClick={onClose}>
            {t("quote.back")}
          </BackButton>
          <ExportButton type="button" onClick={handleExport} disabled={!canExport}>
            <ExportIcon src={download} alt="" $isDisabled={!canExport} />
            {t("quote.export")}
          </ExportButton>
        </Buttons>
      </Modal>
    </Overlay>
  );
};

export default QuoteDetails;
